import { usePureEffect } from "@buzzeasy/shared-frontend-utilities";
import { useState } from "react";

const defaultMapper = <T, TActual = T>(actualState: TActual) => actualState as T extends TActual ? T : never;

type HookReturnValue<T> = [T, React.Dispatch<React.SetStateAction<T>>];

/**
 * Allows the use of a state that is dependant on another and is automatically updated when the actual state changes.
 * @param actualState The actual state that is used.
 * @param mapper An optional function that maps the actual state to the resulting state.
 */
export default function useDependantState<TActual, T = TActual>(actualState: TActual, mapper: (actualState: TActual) => T = defaultMapper): HookReturnValue<T> {
  const internalState = useState<T>(mapper(actualState));

  usePureEffect(
    (refs) => {
      refs.setInternalState(refs.mapper(actualState));
    },
    [actualState],
    { mapper, setInternalState: internalState[1] },
  );

  return internalState as HookReturnValue<T>;
}
